import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Link, Typography } from "@mui/material";

import { LAYERS } from "../constants";
import { formatDate, formatMoney } from "../helpers";
import { brokersById, claimsByProperty, exposuresByProperty, policiesById } from "../portfolioData";
import ClaimsSection from "./ClaimsSection";
import FieldList from "./FieldList";

function PropertyDetail({ property, language, onOpen }) {
  const { t } = useTranslation();
  const broker = brokersById[property.broker_id];
  const exposures = exposuresByProperty[property.id] || [];
  const claims = claimsByProperty[property.id] || [];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {property.address}
      </Typography>
      <FieldList
        rows={[
          { label: t("portfolioExplorer.fields.postalCode"), value: property.postal_code },
          { label: t("portfolioExplorer.fields.city"), value: property.city },
          { label: t("portfolioExplorer.fields.region"), value: property.region },
          { label: t("portfolioExplorer.fields.country"), value: property.country },
          { label: t("portfolioExplorer.fields.propertyType"), value: t(`portfolioExplorer.enums.propertyType.${property.property_type}`) },
          { label: t("portfolioExplorer.fields.usage"), value: t(`portfolioExplorer.enums.usage.${property.usage}`) },
          { label: t("portfolioExplorer.fields.constructionYear"), value: property.construction_year },
          { label: t("portfolioExplorer.fields.constructionType"), value: t(`portfolioExplorer.enums.constructionType.${property.construction_type}`) },
          { label: t("portfolioExplorer.fields.floors"), value: property.floors },
          { label: t("portfolioExplorer.fields.propertyFloor"), value: property.property_floor },
          { label: t("portfolioExplorer.fields.area"), value: `${property.area_sqm} m²` },
          { label: t("portfolioExplorer.fields.basement"), value: property.basement ? t("portfolioExplorer.yes") : t("portfolioExplorer.no") },
          { label: t("portfolioExplorer.fields.insuredValue"), value: formatMoney(property.insured_value, language) },
          { label: t("portfolioExplorer.fields.contentsValue"), value: formatMoney(property.contents_value, language) },
          { label: t("portfolioExplorer.fields.totalInsuredValue"), value: formatMoney(property.total_insured_value, language) },
          { label: t("portfolioExplorer.fields.premium"), value: formatMoney(property.premium, language) },
          { label: t("portfolioExplorer.fields.occupancy"), value: t(`portfolioExplorer.enums.occupancy.${property.occupancy}`) },
        ]}
      />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t("portfolioExplorer.broker")}
      </Typography>
      {broker && (
        <Link component="button" type="button" onClick={() => onOpen(LAYERS.broker, broker.id)} sx={{ mb: 2 }}>
          {broker.name}
        </Link>
      )}

      <ClaimsSection claims={claims} language={language} kind="property" />

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
        {t("portfolioExplorer.exposures")}
      </Typography>
      {exposures.map((exposure) => {
        const policy = policiesById[exposure.policy_id];
        return (
          <Box key={exposure.policy_id} sx={{ mb: 1.5, p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{policy?.policy_number}</Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t(`portfolioExplorer.enums.status.${policy?.status}`)} · {t(`portfolioExplorer.enums.coverageType.${exposure.coverage_type}`)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.policy.building")}: {formatMoney(exposure.building_sum_insured, language)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.policy.contents")}: {formatMoney(exposure.contents_sum_insured, language)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.policy.businessInterruption")}: {formatMoney(exposure.business_interruption_sum_insured, language)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.policy.deductible")}: {formatMoney(exposure.deductible, language)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.fields.premium")}: {formatMoney(exposure.premium, language)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.policy.insuredPeriod")}: {formatDate(exposure.insured_from, language)} – {formatDate(exposure.insured_to, language)}
            </Typography>
            {exposure.cancelled_at && (
              <Typography variant="caption" color="text.secondary" component="div">
                {t("portfolioExplorer.policy.cancelled")}: {formatDate(exposure.cancelled_at, language)}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default PropertyDetail;
